/* eslint-disable no-unused-vars */
import { loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import SupportPresentIcon from '@/assets/all-icons/support-present-icon';
import config from '@/config';
import stripeConfig, { resolveStripePublishableKey } from '@/config/stripe.config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import { normaliseSupportConfig } from '@/layouts/navigation/user-profile/support/constants';
import SupportPublicPopup from '@/layouts/navigation/user-profile/support/SupportPublicPopup';
import ConversationInput from '@/pages/orders/order-details/conversation/components/ConversationInput';
import BoardVirtualList from '@/pages/profile/board/components/BoardVirtualList';
import PostVirtualList from '@/pages/profile/board/components/PostVirtualList';
import ChannelMessagesService from '@/services/channelMessages.service';
import ChannelsService from '@/services/channels.service';
import fetchWrapper from '@/services/fetchWrapper.service';
import SupportService from '@/services/support.service';
import ChatMessage from '@/shared-components/cards/components/ChatMessage';
import ChatTimestamp from '@/shared-components/cards/components/ChatTimestamp';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import { launchSupportCompletionConfetti } from '@/shared-components/effects/components/supportCompletionConfetti';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import InfoIcon from '@/shared-components/icons/components/public/InfoIcon';
import Loader from '@/shared-components/loader/Loader';
import useChannelStickers from '@/shared-components/stickers/useChannelStickers';
import Utils from '@/utils';

import {
  areSummaryEntriesEqual,
  buildMessagePreviewText,
  cloneUser,
  deriveUnreadMessageIds,
  getMessageIdentifier,
  getTimeValue,
  markChannelMessageAsReadLocally,
  mergeMessages,
  normalizeChannelMessage,
  resolveChannelOwnerProfileType,
  resolveImageId,
  resolveUserId,
} from './utils/channelMessageHelpers';
import EmptyChannel from './EmptyChannel';

import './styles/ChannelChat.css';

const SUMMARY_FALLBACK_INTERVAL = 30000; // Fallback polling when membership summary is unavailable
const MESSAGE_DELETE_WINDOW_MS = 5 * 60 * 1000;
const INITIAL_MESSAGE_PAGE_SIZE = 20;
const INCREMENTAL_MESSAGE_PAGE_SIZE = 50;
const HISTORY_PREFETCH_THRESHOLD_PX = 420;
const OLDER_HISTORY_LOADER_DELAY_MS = 180;
const LATEST_PAGE_CURSOR_ISO = '9999-12-31T23:59:59.999Z';

function resolveVipView(search) {
  const value = new URLSearchParams(search || '').get('view');
  if (value === 'posts' || value === 'articles') {
    return value;
  }
  return 'conversation';
}

const VIP_AUDIENCE_VALUES = new Set([
  'vip_only',
  'vip',
  'studio_only',
  'studio',
  'subscriber_only',
  'subscribers_only',
]);

const PUBLIC_AUDIENCE_VALUES = new Set([
  'public',
  'everyone',
  'all',
]);

function normalizeAudienceValue(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (!normalized) {
    return null;
  }
  if (VIP_AUDIENCE_VALUES.has(normalized)) {
    return 'vip_only';
  }
  if (PUBLIC_AUDIENCE_VALUES.has(normalized)) {
    return 'public';
  }
  return null;
}

function resolveContentAudience(item) {
  const candidates = [
    item?.post_container?.audience,
    item?.audience,
    item?.post_container?.visibility,
    item?.visibility,
    item?.post_container?.access,
    item?.access,
  ];

  for (const candidate of candidates) {
    const resolved = normalizeAudienceValue(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return null;
}

function isVipOnlyAudience(item) {
  return resolveContentAudience(item) === 'vip_only';
}

function resolveBooleanFlag(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;
    if (['true', '1', 'yes', 'on', 'enabled', 'active', 'published'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'off', 'disabled', 'inactive', 'draft'].includes(normalized)) {
      return false;
    }
  }
  return null;
}

function resolveFirstBoolean(candidates, fallback = false) {
  for (const candidate of candidates) {
    const value = resolveBooleanFlag(candidate);
    if (value !== null) {
      return value;
    }
  }
  return fallback;
}

export default function ChannelChat({ messagesType }) {
  const { user_username, creator_username, ownerUsername, channelId } = useParams();
  const channelOwner = user_username || creator_username || ownerUsername;
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations('common.profile.buttons');
  const { state, stateHandler, cacheHandler } = useStateHandler();
  const stripeInstanceRef = useRef(null);
  const [channelMembership, setChannelMembership] = useState(null);
  const [ownerMeta, setOwnerMeta] = useState({ ownerId: null, ownerType: null });
  const [vipContent, setVipContent] = useState({ loading: false, error: null, data: [], totalDocs: 0 });
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [channelSupportState, setChannelSupportState] = useState({
    ownerId: null,
    isPublished: false,
    channelSupportEnabled: false,
  });
  const [supportConfig, setSupportConfig] = useState(null);
  const [supportEnabled, setSupportEnabled] = useState(false);
  const [supportTargetUser, setSupportTargetUser] = useState(null);
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [supportRequest, setSupportRequest] = useState({ loading: false, error: null });

  const vipView = useMemo(() => resolveVipView(location.search), [location.search]);

  const channelKey = channelId ? String(channelId) : null;
  const channelSummary = channelKey ? state?.channelSummaries?.[channelKey] || null : null;
  const channelCreatorUsername = useMemo(() => (
    channelSummary?.ownerUsername
    || channelSummary?.channel?.owner_username
    || channelSummary?.channel?.owner?.user_username
    || channelOwner
    || null
  ), [channelOwner, channelSummary]);
  const stickerOwnerUsername = useMemo(
    () => channelCreatorUsername || channelOwner || null,
    [channelCreatorUsername, channelOwner]
  );
  const { stickers: channelStickers, stickersLoading: channelStickersLoading } = useChannelStickers(
    channelId,
    stickerOwnerUsername
  );
  const supportOwnerId = useMemo(
    () => channelSupportState?.ownerId || ownerMeta?.ownerId || null,
    [channelSupportState?.ownerId, ownerMeta?.ownerId]
  );
  const defaultSupportConfig = useMemo(() => normaliseSupportConfig(null), []);

  const stateHandlerRef = useRef(stateHandler);
  useEffect(() => {
    stateHandlerRef.current = stateHandler;
  }, [stateHandler]);

  const updateChannelSummary = useCallback((patch) => {
    if (!channelKey || !patch) return;
    const handler = stateHandlerRef.current;
    if (!handler) return;
    const summaries = handler.getValue('channelSummaries') || {};
    const previousEntry = summaries[channelKey] || {};

    const nextEntry = {
      ...previousEntry,
      channelId: channelKey,
      ...patch,
    };

    if (patch.lastMessage) {
      nextEntry.lastMessage = patch.lastMessage;
    }
    if (patch.readState) {
      nextEntry.readState = {
        ...(previousEntry.readState || {}),
        ...patch.readState,
      };
    }
    if (patch.unreadCount === undefined && previousEntry.unreadCount !== undefined && nextEntry.unreadCount === undefined) {
      nextEntry.unreadCount = previousEntry.unreadCount;
    }
    nextEntry.lastActivity = patch.lastActivity
      || nextEntry?.lastMessage?.createdAt
      || nextEntry?.lastMessage?.created_at
      || previousEntry.lastActivity
      || null;
    if (patch.previewText !== undefined) {
      nextEntry.previewText = patch.previewText;
    }

    if (!areSummaryEntriesEqual(previousEntry, nextEntry)) {
      handler.set('channelSummaries', {
        ...summaries,
        [channelKey]: nextEntry,
      });
    }
  }, [channelKey]);

  const signalGlobalUnreadUpdate = useCallback(() => {
    const handler = stateHandlerRef.current;
    if (!handler) return;
    handler.set('channelUnreadUpdated', true);
  }, []);

  const signalNotificationRefresh = useCallback(() => {
    cacheHandler?.triggerAction?.('NOTIFICATION_REFRESH');
  }, [cacheHandler]);

  const markMessageDeleting = useCallback((messageId, active) => {
    if (!messageId) return;
    setDeletingMessageIds((prev) => {
      const next = new Set(prev);
      const key = String(messageId);
      if (active) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const isOwner =
    state &&
    state.user_session &&
    state.user_session.user_username === channelOwner;

  const currentUser = state?.user_session;
  const currentUserId = resolveUserId(currentUser);
  const channelOwnerProfileType = useMemo(() => (
    resolveChannelOwnerProfileType(
      channelSummary?.channel?.channel_kind
      || channelSummary?.channel?.channelKind
      || ownerMeta?.ownerType
      || channelSummary?.ownerType
      || channelSummary?.channel?.owner?.user_type
      || channelSummary?.channel?.owner?.__t
      || (isOwner ? (currentUser?.user_type ?? currentUser?.__t ?? null) : null),
      'gallery'
    )
  ), [channelSummary, currentUser?.user_type, currentUser?.__t, isOwner, ownerMeta?.ownerType]);

  useEffect(() => {
    let cancelled = false;

    const summaryOwnerId =
      channelSummary?.ownerId
      || channelSummary?.channel?.owner?._id
      || channelSummary?.channel?.owner
      || null;
    const summaryOwnerType =
      channelSummary?.channel?.channel_kind
      || channelSummary?.channel?.channelKind
      || channelSummary?.ownerType
      || channelSummary?.channel?.owner?.user_type
      || channelSummary?.channel?.owner?.__t
      || null;

    if (summaryOwnerId && summaryOwnerType) {
      setOwnerMeta({
        ownerId: String(summaryOwnerId),
        ownerType: resolveChannelOwnerProfileType(summaryOwnerType, 'gallery'),
      });
      return () => {
        cancelled = true;
      };
    }

    if (isOwner && currentUserId) {
      const ownType = resolveChannelOwnerProfileType(
        currentUser?.user_type ?? currentUser?.__t ?? null,
        'gallery'
      );
      setOwnerMeta({ ownerId: String(currentUserId), ownerType: ownType });
      return () => {
        cancelled = true;
      };
    }

    if (!channelOwner) {
      setOwnerMeta({ ownerId: null, ownerType: null });
      return () => {
        cancelled = true;
      };
    }

    fetchWrapper
      .get(`${config.apis.api.url}/user/public/${encodeURIComponent(channelOwner)}`, { injectToken: true })
      .then(({ response, data }) => {
        if (cancelled || !response?.ok || !data?._id) return;
        setOwnerMeta({
          ownerId: String(data._id),
          ownerType: resolveChannelOwnerProfileType(data.user_type ?? data.__t ?? null, 'gallery'),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setOwnerMeta({ ownerId: null, ownerType: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [channelOwner, channelSummary, currentUser?.user_type, currentUser?.__t, currentUserId, isOwner]);

  const membershipRole = useMemo(() => (
    channelMembership?.membershipRole
    ?? channelMembership?.role
    ?? channelMembership?.memberRole
    ?? channelMembership?.membership_role
    ?? channelMembership?.member_role
    ?? null
  ), [channelMembership]);

  const membershipPermissions = useMemo(() => (
    channelMembership?.permissions
    ?? channelMembership?.membershipPermissions
    ?? channelMembership?.permissionsList
    ?? null
  ), [channelMembership]);

  const normalizedMembershipRole = useMemo(
    () => (typeof membershipRole === 'string' ? membershipRole.toLowerCase() : null),
    [membershipRole]
  );

  const hasModerationPermission = useMemo(() => {
    if (isOwner) return true;
    if (normalizedMembershipRole && ['member', 'owner', 'admin', 'moderator', 'manager'].includes(normalizedMembershipRole)) {
      return true;
    }
    if (Array.isArray(membershipPermissions)) {
      const normalized = membershipPermissions
        .filter(Boolean)
        .map((perm) => String(perm).toLowerCase());
      if (normalized.some((perm) => ['manage_messages', 'moderate', 'delete_messages'].includes(perm))) {
        return true;
      }
    }
    return false;
  }, [isOwner, normalizedMembershipRole, membershipPermissions]);

  const resolvedChannelType = useMemo(() => {
    const candidates = [
      channelSummary?.channelType,
      channelSummary?.channel_type,
      channelSummary?.type,
      channelSummary?.membershipType,
      channelSummary?.membership_type,
      channelSummary?.channel?.channelType,
      channelSummary?.channel?.channel_type,
      channelSummary?.channel?.type,
      channelSummary?.channel?.membershipType,
      channelMembership?.channelType,
      channelMembership?.channel_type,
      channelMembership?.type,
      channelMembership?.membershipType,
      channelMembership?.membership_type,
    ];
    for (const value of candidates) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return null;
  }, [channelSummary, channelMembership]);

  const normalizedChannelType = useMemo(() => (
    resolvedChannelType ? resolvedChannelType.toLowerCase() : null
  ), [resolvedChannelType]);

  const canShareMedia = useMemo(() => {
    if (!currentUserId) return false;
    if (isOwner) return true;
    if (!normalizedChannelType) return false;
    return normalizedChannelType.includes('subscriber');
  }, [currentUserId, isOwner, normalizedChannelType]);

  const [mediaUploadInProgress, setMediaUploadInProgress] = useState(false);

  useEffect(() => {
    document.body.classList.add('chat-page');
    return () => document.body.classList.remove('chat-page');
  }, []);

  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [channelName, setChannelName] = useState(channelOwner);
  const [channelImage, setChannelImage] = useState(null);
  const [deletingMessageIds, setDeletingMessageIds] = useState(() => new Set());
  const [unreadMarkerId, setUnreadMarkerId] = useState(null);
  const [targetMessageId, setTargetMessageId] = useState(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [showOlderMessagesLoader, setShowOlderMessagesLoader] = useState(false);
  const headerStateRef = useRef({ priority: -Infinity });
  const profileFetchRequestRef = useRef(0);
  const applyHeaderDetails = useCallback((details, priority = 0) => {
    if (!details || typeof details !== 'object') return;
    const nextPriority = typeof priority === 'number' ? priority : 0;
    const previous = headerStateRef.current || { priority: -Infinity };
    const previousPriority = typeof previous.priority === 'number' ? previous.priority : -Infinity;
    if (nextPriority < previousPriority) {
      return;
    }
    const nextState = { ...previous, ...details, priority: nextPriority };
    headerStateRef.current = nextState;
    if (Object.prototype.hasOwnProperty.call(details, 'name')) {
      setChannelName(details.name ?? '');
    }
    if (Object.prototype.hasOwnProperty.call(details, 'image')) {
      setChannelImage(details.image ?? null);
    }
  }, [setChannelImage, setChannelName]);
  const [pendingUnreadIds, setPendingUnreadIds] = useState([]);
  const [firstUnreadId, setFirstUnreadId] = useState(null);

  const messagesContainerRef = useRef(null);
  const messageRefs = useRef(new Map());
  const summarySnapshotRef = useRef(null);
  const firstUnreadIdRef = useRef(null);
  const lastUnreadKeyRef = useRef('');
  const unreadMarkerRef = useRef(null);
  const isMountedRef = useRef(true);
  const fetchInFlightRef = useRef(null);
  const messagesStateRef = useRef({ data: [] });
  const initialFetchDoneRef = useRef(false);
  const fallbackPollRef = useRef(null);
  const lastFetchedAtRef = useRef(0);
  const lastFetchedIsoRef = useRef(null);
  const lastReadSyncKeyRef = useRef('');
  const targetMessageTimeoutRef = useRef(null);
  const olderHistoryPaginationRef = useRef({ beforeCursor: null, hasOlder: false });
  const loadingOlderMessagesRef = useRef(false);
  const olderLoaderDelayTimeoutRef = useRef(null);
  const preserveScrollOnNextMessagesRenderRef = useRef(false);
  const pendingHistoryAnchorRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => () => {
    if (targetMessageTimeoutRef.current) {
      window.clearTimeout(targetMessageTimeoutRef.current);
      targetMessageTimeoutRef.current = null;
    }
    if (olderLoaderDelayTimeoutRef.current) {
      window.clearTimeout(olderLoaderDelayTimeoutRef.current);
      olderLoaderDelayTimeoutRef.current = null;
    }
  }, []);

  const isFromProfile =
    new URLSearchParams(location.search).get('fromProfile') === 'true';

  const handleBack = () => {
    if (window.innerWidth <= 650 && isFromProfile) {
      if (isOwner) navigate('/profile', { replace: true });
      else navigate(`/user/${channelOwner}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (!channelOwner) return;
    applyHeaderDetails({ name: channelOwner }, 0);
  }, [channelOwner, applyHeaderDetails]);

  useEffect(() => {
    // Reset header state when switching channel/owner to prevent stale bleed.
    profileFetchRequestRef.current += 1;
    headerStateRef.current = { priority: -Infinity };
    setChannelName(channelOwner || '');
    setChannelImage(null);
    setShowSupportPopup(false);
    setSupportRequest({ loading: false, error: null });
    setSupportConfig(null);
    setSupportEnabled(false);
    setSupportTargetUser(null);
    setChannelSupportState({
      ownerId: null,
      isPublished: false,
      channelSupportEnabled: false,
    });
  }, [channelId, channelOwner]);

  useEffect(() => {
    messageRefs.current = new Map();
    firstUnreadIdRef.current = null;
    lastUnreadKeyRef.current = '';
    setPendingUnreadIds([]);
    setFirstUnreadId(null);
    setUnreadMarkerId(null);
    setTargetMessageId(null);
    unreadMarkerRef.current = null;
    messagesStateRef.current = { data: [] };
    setMessages({ data: [], totalDocs: 0 });
    lastFetchedAtRef.current = 0;
    lastFetchedIsoRef.current = null;
    olderHistoryPaginationRef.current = { beforeCursor: null, hasOlder: false };
    loadingOlderMessagesRef.current = false;
    preserveScrollOnNextMessagesRenderRef.current = false;
    pendingHistoryAnchorRef.current = null;
    setLoadingOlderMessages(false);
    setShowOlderMessagesLoader(false);
    if (olderLoaderDelayTimeoutRef.current) {
      window.clearTimeout(olderLoaderDelayTimeoutRef.current);
      olderLoaderDelayTimeoutRef.current = null;
    }
    summarySnapshotRef.current = channelSummary || null;
    initialFetchDoneRef.current = false;

    let cancelled = false;
    // Optimist: marca como leído localmente para evitar parpadeos
    updateChannelSummary({ unreadCount: 0 });
    setLoading(true);
    setError(null);

    fetchMessages({ reset: true, withLoader: true }).then(() => {
      if (!cancelled) {
        initialFetchDoneRef.current = true;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [channelId]);

  useEffect(() => {
    if (!channelSummary) return;
    const details = {};
    const summaryTitle =
      channelSummary.title
      || channelSummary.name
      || channelSummary?.channel?.title
      || channelSummary?.channel?.channel_name
      || channelOwner
      || null;
    if (summaryTitle) {
      details.name = summaryTitle;
    }
    const summaryImage =
      resolveImageId(channelSummary?.owner?.user_image)
      || resolveImageId(channelSummary?.channel?.owner?.user_image)
      || resolveImageId(channelSummary?.ownerImage)
      || resolveImageId(channelSummary?.owner_image)
      || null;
    if (summaryImage) {
      details.image = summaryImage;
    } else {
      const summaryAvatar = resolveImageId(channelSummary?.avatar);
      const summaryCover =
        resolveImageId(channelSummary?.channel?.image)
        || resolveImageId(channelSummary?.image)
        || resolveImageId(channelSummary?.channelImage)
        || null;
      if (summaryAvatar && (!summaryCover || summaryAvatar !== summaryCover)) {
        details.image = summaryAvatar;
      }
    }
    if (Object.keys(details).length > 0) {
      applyHeaderDetails(details, 2);
    }
  }, [channelSummary, channelOwner, applyHeaderDetails]);

  useEffect(() => {
    let mounted = true;
    async function checkMembership() {
      if (!ownerUsername || !channelId) return;
      if (isOwner) return;
      try {
        const { response, data } = await ChannelsService.getMemberOf('me');
        if (!mounted) return;
        if (response?.ok) {
          const list = Array.isArray(data?.data) ? data.data : [];
          const membershipEntry = list.find((ch) => String(ch?._id) === String(channelId)) || null;
          setChannelMembership(membershipEntry);
          const isMember = Boolean(membershipEntry);
          if (!isMember) {
            navigate(`/channel/${ownerUsername}/${channelId}/join`, { replace: true });
          }
        }
      } catch (e) {
        // silencioso
      }
    }
    checkMembership();
    return () => { mounted = false; };
  }, [ownerUsername, channelId, isOwner, navigate]);

  const fetchProfile = useCallback(() => {
    if (!channelOwner) return;
    const requestId = profileFetchRequestRef.current + 1;
    profileFetchRequestRef.current = requestId;
    const endpoint = isOwner
      ? `${config.apis.api.url}/user/me`
      : `${config.apis.api.url}/user/public/${encodeURIComponent(channelOwner)}`;
    fetchWrapper
      .get(endpoint, { injectToken: true })
      .then(({ response, data }) => {
        if (requestId !== profileFetchRequestRef.current) return;
        if (!response?.ok) {
          return;
        }
        if (data && typeof data === 'object') {
          setSupportTargetUser((prev) => {
            const previous = prev && typeof prev === 'object' ? prev : {};
            const resolvedId = data?._id || previous?._id || supportOwnerId || null;
            return {
              ...previous,
              ...data,
              ...(resolvedId ? { _id: String(resolvedId) } : {}),
            };
          });
        }
        const details = {};
        const imageCandidate = resolveImageId(data?.user_image);
        if (imageCandidate) {
          details.image = imageCandidate;
        }
        if (Object.keys(details).length > 0) {
          // Authoritative source for profile avatar in chat header.
          applyHeaderDetails(details, 4);
        }
      })
      .catch(() => {});
  }, [applyHeaderDetails, channelOwner, isOwner, supportOwnerId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    let mounted = true;
    async function loadChannel() {
      if (!channelId) return;
      try {
        const { response, data } = await ChannelsService.getOne(channelId);
        if (!mounted) return;
        if (response?.ok && data) {
          const channelPayload = data?.data && !Array.isArray(data.data) ? data.data : data;
          const ownerPayload = channelPayload?.owner && typeof channelPayload.owner === 'object'
            ? channelPayload.owner
            : null;
          const ownerIdCandidate =
            ownerPayload?._id
            || (typeof channelPayload?.owner === 'string' ? channelPayload.owner : null)
            || null;

          const details = {};
          const titleCandidate =
            channelPayload?.title
            || channelPayload?.channel_name
            || channelPayload?.name
            || channelPayload?.channel?.title
            || channelPayload?.channel?.channel_name
            || null;
          if (titleCandidate) {
            details.name = titleCandidate;
          }
          const imageCandidate =
            resolveImageId(channelPayload?.owner?.user_image)
            || resolveImageId(channelPayload?.channel?.owner?.user_image)
            || null;
          if (imageCandidate) {
            details.image = imageCandidate;
          }

          const isPublished = resolveFirstBoolean(
            [
              channelPayload?.is_published,
              channelPayload?.published,
              channelPayload?.isPublished,
              channelPayload?.channel?.is_published,
              channelPayload?.channel?.published,
              channelSummary?.channel?.is_published,
              channelSummary?.channel?.published,
            ],
            true
          );

          const channelSupportEnabled = resolveFirstBoolean(
            [
              channelPayload?.channel_support_enabled,
              channelPayload?.channelSupportEnabled,
              channelPayload?.support_enabled_for_channel,
              channelPayload?.channel?.channel_support_enabled,
              channelPayload?.channel?.channelSupportEnabled,
              channelSummary?.channel?.channel_support_enabled,
              channelSummary?.channel?.channelSupportEnabled,
            ],
            true
          );

          setChannelSupportState({
            ownerId: ownerIdCandidate ? String(ownerIdCandidate) : null,
            isPublished: Boolean(isPublished),
            channelSupportEnabled: Boolean(channelSupportEnabled),
          });

          if (ownerPayload || ownerIdCandidate) {
            setSupportTargetUser((prev) => {
              const previous = prev && typeof prev === 'object' ? prev : {};
              const resolvedId = ownerPayload?._id || ownerIdCandidate || previous?._id || null;
              return {
                ...previous,
                ...(ownerPayload || {}),
                ...(resolvedId ? { _id: String(resolvedId) } : {}),
              };
            });
          }

          if (Object.keys(details).length > 0) {
            applyHeaderDetails(details, 3);
          }
        }
      } catch (err) {
        // silencioso
      }
    }
    loadChannel();
    return () => { mounted = false; };
  }, [channelId, channelSummary]);

  useEffect(() => {
    const onFocus = () => fetchProfile();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchProfile]);

  const supportPopupUser = useMemo(() => {
    const source = supportTargetUser && typeof supportTargetUser === 'object' ? supportTargetUser : {};
    const resolvedId = source?._id || supportOwnerId || null;
    const resolvedUsername =
      source?.user_username
      || channelCreatorUsername
      || channelOwner
      || null;
    const resolvedName =
      source?.user_name
      || source?.name
      || source?.artist_name
      || source?.gallery_name
      || (typeof channelName === 'string' && channelName.trim() ? channelName : null)
      || resolvedUsername
      || null;

    if (!resolvedId && !resolvedUsername && !resolvedName) {
      return null;
    }

    return {
      ...source,
      ...(resolvedId ? { _id: String(resolvedId) } : {}),
      user_username: resolvedUsername || source?.user_username || null,
      user_name: resolvedName || source?.user_name || null,
    };
  }, [channelCreatorUsername, channelName, channelOwner, supportOwnerId, supportTargetUser]);

  const canShowSupportIcon = useMemo(() => (
    Boolean(
      !isOwner
      && supportEnabled
      && supportOwnerId
      && channelSupportState?.isPublished
      && channelSupportState?.channelSupportEnabled
    )
  ), [
    channelSupportState?.channelSupportEnabled,
    channelSupportState?.isPublished,
    isOwner,
    supportEnabled,
    supportOwnerId,
  ]);

  useEffect(() => {
    if (!showSupportPopup) {
      setSupportRequest({ loading: false, error: null });
    }
  }, [showSupportPopup]);

  useEffect(() => {
    if (!canShowSupportIcon) {
      setShowSupportPopup(false);
    }
  }, [canShowSupportIcon]);

  useEffect(() => {
    let cancelled = false;

    if (!supportOwnerId || !channelSupportState?.isPublished || !channelSupportState?.channelSupportEnabled || isOwner) {
      setSupportConfig(null);
      setSupportEnabled(false);
      return () => {
        cancelled = true;
      };
    }

    SupportService.getConfiguration(supportOwnerId)
      .then(({ response, data }) => {
        if (cancelled) return;
        if (!response?.ok || !data) {
          setSupportConfig(null);
          setSupportEnabled(false);
          return;
        }
        const normalizedConfig = normaliseSupportConfig(data);
        const isEnabled = Boolean(
          data?.support_enabled
          ?? data?.enabled
          ?? normalizedConfig?.enabled
        );
        setSupportConfig(normalizedConfig);
        setSupportEnabled(isEnabled);
      })
      .catch(() => {
        if (!cancelled) {
          setSupportConfig(null);
          setSupportEnabled(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    channelSupportState?.channelSupportEnabled,
    channelSupportState?.isPublished,
    isOwner,
    supportOwnerId,
  ]);

  const handleSupportConfirm = useCallback(async (payload = {}) => {
    const supportedId = supportPopupUser?._id ? String(supportPopupUser._id) : (supportOwnerId ? String(supportOwnerId) : null);
    if (!state?.user_session?._id) {
      setSupportRequest({ loading: false, error: t('errors.sign_in_required') });
      return;
    }
    if (!supportedId) {
      setSupportRequest({ loading: false, error: t('errors.profile_not_identified') });
      return;
    }

    try {
      setSupportRequest({ loading: true, error: null });
      const body = {
        amount: payload.amount,
        currency: payload.currency ?? 'usd',
        vatAmount: payload.vatAmount ?? 0,
        vatRate: payload.vatRate ?? 0,
        isRecurring: payload.frequency === 'monthly',
        anonymous: Boolean(payload.giveAnonymously),
        paymentMethodId: payload.paymentMethodId ?? payload.paymentMethod?.id ?? null,
      };

      const { response, data } = await SupportService.createPaymentIntent(supportedId, body);
      if (!response?.ok) {
        throw new Error(data?.message ?? t('errors.support_payment_failed'));
      }

      const paymentIntentId = data?.payment_intent_id;
      if (!paymentIntentId) {
        throw new Error(t('errors.payout_init_failed'));
      }

      const shouldConfirm =
        data?.client_secret
        && (data?.requires_action
          || ['requires_action', 'requires_confirmation'].includes(data?.status));

      if (shouldConfirm) {
        let stripeInstance = stripeInstanceRef.current;
        if (!stripeInstance) {
          const publishableKey = stripeConfig.publishableKey || (await resolveStripePublishableKey());
          if (!publishableKey) {
            throw new Error(t('errors.stripe_load_failed'));
          }
          stripeInstance = await loadStripe(publishableKey);
          stripeInstanceRef.current = stripeInstance;
        }

        if (!stripeInstance) {
          throw new Error(t('errors.stripe_unavailable'));
        }

        const confirmPayload = {};
        if (body.paymentMethodId) {
          confirmPayload.payment_method = body.paymentMethodId;
        }

        const confirmResult = await stripeInstance.confirmCardPayment(
          data.client_secret,
          confirmPayload
        );

        if (confirmResult?.error) {
          throw confirmResult.error;
        }
      }

      const finalizeResult = await SupportService.finalizeContribution(supportedId, {
        paymentIntentId,
      });

      if (!finalizeResult?.response?.ok) {
        throw new Error(finalizeResult?.data?.message ?? t('errors.support_record_failed'));
      }

      setSupportRequest({ loading: false, error: null });
      setShowSupportPopup(false);
      launchSupportCompletionConfetti();
      stateHandler.set('temporalPopup', {
        text: body.isRecurring ? t('messages.support_recurring_scheduled') : t('messages.support_sent'),
      });
    } catch (error) {
      const message = error?.message ?? t('errors.support_process_failed');
      setSupportRequest({ loading: false, error: message });
    }
  }, [state?.user_session?._id, stateHandler, supportOwnerId, supportPopupUser?._id, t]);

  const fetchMessages = useCallback(
    async ({ reset = false, withLoader = false, force = false } = {}) => {
      if (!channelId) {
        if (withLoader && isMountedRef.current) setLoading(false);
        messagesStateRef.current = { data: [] };
        return Promise.resolve(true);
      }

      const activeRequest = fetchInFlightRef.current;
      if (activeRequest) {
        if (!force) {
          return activeRequest;
        }
        try {
          await activeRequest;
        } catch (err) {
          // silencioso
        }
      }

      if (reset) {
        lastFetchedAtRef.current = 0;
        lastFetchedIsoRef.current = null;
      }

      if (withLoader && isMountedRef.current) {
        setLoading(true);
      }

      const params = {};
      if (reset) {
        params.before = LATEST_PAGE_CURSOR_ISO;
        params.limit = INITIAL_MESSAGE_PAGE_SIZE;
      } else if (lastFetchedIsoRef.current) {
        params.after = lastFetchedIsoRef.current;
        params.limit = INCREMENTAL_MESSAGE_PAGE_SIZE;
      }

      const requestPromise = (async () => {
        let success = false;
        try {
          const { response, data } = await ChannelMessagesService.getByChannel(channelId, params);
          if (!isMountedRef.current) return false;

          if (response?.ok) {
            const rawList = Array.isArray(data?.data) ? [...data.data] : [];
            const normalizedList = rawList
              .map((message) => normalizeChannelMessage(message, { currentUser }))
              .filter(Boolean);

            const existing = Array.isArray(messagesStateRef.current?.data)
              ? messagesStateRef.current.data
              : [];
            const merged = reset ? normalizedList : mergeMessages(existing, normalizedList);

            const payload = { data: merged, totalDocs: merged.length };
            messagesStateRef.current = payload;
            setMessages(payload);
            setError(null);

            const latestMessage = merged.length > 0 ? merged[merged.length - 1] : null;
            const meta = data?.meta && typeof data.meta === 'object' ? data.meta : {};
            const paginationMeta = meta?.pagination && typeof meta.pagination === 'object' ? meta.pagination : null;
            const metaUnread = typeof meta.unreadCount === 'number' && Number.isFinite(meta.unreadCount)
              ? Math.max(0, meta.unreadCount)
              : undefined;
            const metaReadState = meta.readState || null;

            if (reset) {
              const oldestLoaded = merged.length > 0 ? merged[0] : null;
              olderHistoryPaginationRef.current = {
                beforeCursor: paginationMeta?.beforeCursor || oldestLoaded?.createdAt || oldestLoaded?.created_at || null,
                hasOlder: Boolean(paginationMeta?.hasOlder),
              };
            }

            if (latestMessage?.createdAt) {
              const latestTime = new Date(latestMessage.createdAt).getTime();
              if (!Number.isNaN(latestTime)) {
                lastFetchedAtRef.current = latestTime;
              }
              lastFetchedIsoRef.current = new Date(latestMessage.createdAt).toISOString();
            }

            updateChannelSummary({
              lastMessage: latestMessage ? { ...latestMessage } : null,
              lastActivity: latestMessage ? (latestMessage.createdAt ?? latestMessage.created_at ?? null) : null,
              previewText: buildMessagePreviewText(latestMessage),
              unreadCount: metaUnread,
              readState: metaReadState || undefined,
            });

            success = true;
          } else if (response?.status === 404) {
            const emptyPayload = { data: [], totalDocs: 0 };
            messagesStateRef.current = emptyPayload;
            setMessages(emptyPayload);
            setError(null);
            success = true;
          } else {
            setError('Failed to load conversation.');
          }
        } catch (err) {
          if (isMountedRef.current) {
            setError('Failed to load conversation.');
          }
        } finally {
          if (withLoader && isMountedRef.current) {
            setLoading(false);
          }
        }

        return success;
      })();

      fetchInFlightRef.current = requestPromise;

      try {
        return await requestPromise;
      } finally {
        if (fetchInFlightRef.current === requestPromise) {
          fetchInFlightRef.current = null;
        }
      }
    },
    [channelId, currentUser]
  );

  const loadOlderMessages = useCallback(async () => {
    if (!channelId || loadingOlderMessagesRef.current) return;

    const pagination = olderHistoryPaginationRef.current || {};
    if (!pagination?.hasOlder || !pagination?.beforeCursor) return;

    loadingOlderMessagesRef.current = true;
    setLoadingOlderMessages(true);
    if (olderLoaderDelayTimeoutRef.current) {
      window.clearTimeout(olderLoaderDelayTimeoutRef.current);
    }
    olderLoaderDelayTimeoutRef.current = window.setTimeout(() => {
      if (isMountedRef.current && loadingOlderMessagesRef.current) {
        setShowOlderMessagesLoader(true);
      }
      olderLoaderDelayTimeoutRef.current = null;
    }, OLDER_HISTORY_LOADER_DELAY_MS);

    const container = messagesContainerRef.current;
    if (!container) {
      loadingOlderMessagesRef.current = false;
      setLoadingOlderMessages(false);
      setShowOlderMessagesLoader(false);
      if (olderLoaderDelayTimeoutRef.current) {
        window.clearTimeout(olderLoaderDelayTimeoutRef.current);
        olderLoaderDelayTimeoutRef.current = null;
      }
      return;
    }
    const currentList = messagesStateRef.current?.data || [];
    let anchor = null;
    for (let index = 0; index < currentList.length; index += 1) {
      const message = currentList[index];
      const messageId = getMessageIdentifier(message) || message?.clientSideId || null;
      if (!messageId) continue;
      const node = messageRefs.current.get(String(messageId));
      if (!node) continue;
      const offset = node.offsetTop - container.scrollTop;
      if (offset >= 0) {
        anchor = { id: String(messageId), offset };
        break;
      }
      anchor = { id: String(messageId), offset };
    }
    pendingHistoryAnchorRef.current = anchor;

    try {
      const { response, data } = await ChannelMessagesService.getByChannel(channelId, {
        before: pagination.beforeCursor,
        limit: INITIAL_MESSAGE_PAGE_SIZE,
      });
      if (!isMountedRef.current || !response?.ok) return;

      const rawList = Array.isArray(data?.data) ? [...data.data] : [];
      const normalizedList = rawList
        .map((message) => normalizeChannelMessage(message, { currentUser }))
        .filter(Boolean);

      if (normalizedList.length > 0) {
        preserveScrollOnNextMessagesRenderRef.current = true;
        setMessages((prev) => {
          const previousData = Array.isArray(prev?.data) ? prev.data : [];
          const seen = new Set(previousData
            .map((item) => {
              const identifier = getMessageIdentifier(item);
              return identifier ? String(identifier) : null;
            })
            .filter(Boolean));
          const olderToPrepend = [];
          normalizedList.forEach((item) => {
            const identifier = getMessageIdentifier(item);
            const key = identifier ? String(identifier) : null;
            if (key && seen.has(key)) {
              return;
            }
            if (key) {
              seen.add(key);
            }
            olderToPrepend.push(item);
          });
          const merged = olderToPrepend.length > 0
            ? [...olderToPrepend, ...previousData]
            : previousData;
          const payloadState = { data: merged, totalDocs: merged.length };
          messagesStateRef.current = payloadState;
          return payloadState;
        });
      } else {
        pendingHistoryAnchorRef.current = null;
      }

      const paginationMeta = data?.meta?.pagination && typeof data.meta.pagination === 'object'
        ? data.meta.pagination
        : null;
      const fallbackOldest = normalizedList.length > 0 ? normalizedList[0] : null;
      olderHistoryPaginationRef.current = {
        beforeCursor: paginationMeta?.beforeCursor || fallbackOldest?.createdAt || fallbackOldest?.created_at || pagination.beforeCursor || null,
        hasOlder: Boolean(paginationMeta?.hasOlder),
      };
    } catch (err) {
      // non-fatal
      pendingHistoryAnchorRef.current = null;
    } finally {
      loadingOlderMessagesRef.current = false;
      setLoadingOlderMessages(false);
      setShowOlderMessagesLoader(false);
      if (olderLoaderDelayTimeoutRef.current) {
        window.clearTimeout(olderLoaderDelayTimeoutRef.current);
        olderLoaderDelayTimeoutRef.current = null;
      }
    }
  }, [channelId, currentUser]);

  const handleConversationScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (loadingOlderMessagesRef.current) return;
    const pagination = olderHistoryPaginationRef.current || {};
    if (!pagination?.hasOlder || !pagination?.beforeCursor) return;

    if (container.scrollTop <= HISTORY_PREFETCH_THRESHOLD_PX) {
      loadOlderMessages();
    }
  }, [loadOlderMessages]);

  const deleteMessage = useCallback(async (message) => {
    if (!message) return;
    const identifier = getMessageIdentifier(message);
    if (!identifier) return;
    const idString = String(identifier);
    setReplyToMessage((currentReply) => {
      const replyIdentifier = getMessageIdentifier(currentReply);
      if (replyIdentifier && String(replyIdentifier) === idString) {
        return null;
      }
      return currentReply;
    });
    markMessageDeleting(idString, true);

    let removedMessageRef = null;

    setMessages((prev) => {
      const previousData = Array.isArray(prev?.data) ? prev.data : [];
      const data = [];
      previousData.forEach((item) => {
        const key = getMessageIdentifier(item);
        if (String(key) === idString) {
          removedMessageRef = item;
          return;
        }
        data.push(item);
      });

      const payloadState = {
        ...prev,
        data,
        totalDocs: data.length,
      };
      messagesStateRef.current = payloadState;
      return payloadState;
    });

    let deletionSucceeded = false;

    try {
      const { response } = await ChannelMessagesService.remove(identifier);
      if (!response?.ok) {
        throw new Error('failed');
      }

      deletionSucceeded = true;

      const nextData = Array.isArray(messagesStateRef.current?.data) ? messagesStateRef.current.data : [];
      const nextLast = nextData.length > 0 ? nextData[nextData.length - 1] : null;
      updateChannelSummary({
        lastMessage: nextLast ? { ...nextLast } : null,
        lastActivity: nextLast ? (nextLast.createdAt ?? nextLast.created_at ?? null) : null,
        previewText: buildMessagePreviewText(nextLast),
      });
      signalGlobalUnreadUpdate();
    } catch (err) {
      if (removedMessageRef) {
        setMessages((prev) => {
          const previousData = Array.isArray(prev?.data) ? prev.data : [];
          const data = [...previousData, removedMessageRef];
          data.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          const payloadState = {
            ...prev,
            data,
            totalDocs: data.length,
          };
          messagesStateRef.current = payloadState;
          return payloadState;
        });
      }
    } finally {
      markMessageDeleting(idString, false);
    }

    if (deletionSucceeded) {
      try {
        await fetchMessages({ reset: false, withLoader: false, force: true });
      } catch (err) {
        // silencioso
      }
    }
  }, [fetchMessages, markMessageDeleting, signalGlobalUnreadUpdate, updateChannelSummary]);
  const lastSummaryReactTsRef = useRef(0);

  useEffect(() => {
    const previousSummary = summarySnapshotRef.current;
    const currentSummary = channelSummary || null;
    summarySnapshotRef.current = currentSummary;

    if (!channelKey) {
      summarySnapshotRef.current = null;
      return;
    }

    if (!initialFetchDoneRef.current) {
      return;
    }

    if (!currentSummary) {
      return;
    }

    const lastFetchedTime = lastFetchedAtRef.current || 0;
    const summaryActivity = getTimeValue(currentSummary.lastActivity);
    const unreadChanged = !previousSummary
      || (previousSummary?.unreadCount ?? 0) !== (currentSummary?.unreadCount ?? 0);
    const nowTs = Date.now();
    const tooSoon = nowTs - (lastSummaryReactTsRef.current || 0) < 1200;

    if (!previousSummary) {
      if (summaryActivity > lastFetchedTime || (unreadChanged && summaryActivity > lastFetchedTime && !tooSoon)) {
        fetchMessages({ reset: false });
        lastSummaryReactTsRef.current = nowTs;
      }
      return;
    }

    const previousActivity = getTimeValue(previousSummary.lastActivity);
    const shouldReactToUnread = unreadChanged && summaryActivity > Math.max(lastFetchedTime, previousActivity);
    if ((summaryActivity > lastFetchedTime || summaryActivity > previousActivity || shouldReactToUnread) && !tooSoon) {
      fetchMessages({ reset: false });
      lastSummaryReactTsRef.current = nowTs;
    }
  }, [channelKey, channelSummary, fetchMessages]);

  useEffect(() => {
    if (!channelKey) {
      if (fallbackPollRef.current) {
        clearInterval(fallbackPollRef.current);
        fallbackPollRef.current = null;
      }
      return undefined;
    }

    if (channelSummary) {
      if (fallbackPollRef.current) {
        clearInterval(fallbackPollRef.current);
        fallbackPollRef.current = null;
      }
      return undefined;
    }

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      fetchMessages({ reset: false });
    };

    tick();
    fallbackPollRef.current = window.setInterval(tick, SUMMARY_FALLBACK_INTERVAL);

    return () => {
      if (fallbackPollRef.current) {
        clearInterval(fallbackPollRef.current);
        fallbackPollRef.current = null;
      }
    };
  }, [channelKey, channelSummary, fetchMessages]);

  useEffect(() => {
    const onFocus = () => {
      if (!channelSummary) {
        fetchMessages({ reset: false });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [channelSummary, fetchMessages]);

  useEffect(() => {
    const list = messages?.data;
    if (!channelId || !Array.isArray(list)) {
      firstUnreadIdRef.current = null;
      setFirstUnreadId(null);
      setUnreadMarkerId(null);
      unreadMarkerRef.current = null;
      if (pendingUnreadIds.length !== 0) {
        setPendingUnreadIds([]);
      }
      updateChannelSummary({
        lastMessage: null,
        unreadCount: 0,
        previewText: '',
      });
      return;
    }

    const unreadIds = deriveUnreadMessageIds(list, currentUser, channelSummary?.readState || null);
    const firstUnread = unreadIds[0] || null;
    firstUnreadIdRef.current = firstUnread;
    setFirstUnreadId(firstUnread);

    if (unreadIds.length > 0) {
      const firstUnreadIdString = firstUnread ? String(firstUnread) : null;
      if (firstUnreadIdString) {
        setUnreadMarkerId((prev) => {
          if (!prev) {
            unreadMarkerRef.current = firstUnreadIdString;
            return firstUnreadIdString;
          }
          const markerStillValid = unreadIds.some((id) => String(id) === prev);
          if (!markerStillValid) {
            unreadMarkerRef.current = firstUnreadIdString;
            return firstUnreadIdString;
          }
          return prev;
        });
      }
    }
    const unreadKey = unreadIds.join('|');
    setPendingUnreadIds((prev) => {
      const prevKey = Array.isArray(prev) ? prev.join('|') : '';
      if (prevKey === unreadKey) return prev;
      return unreadIds.slice();
    });
    lastUnreadKeyRef.current = unreadKey;

    const latestMessage = list[list.length - 1] || null;
    updateChannelSummary({
      lastMessage: latestMessage ? { ...latestMessage } : null,
      unreadCount: unreadIds.length,
      lastActivity: latestMessage ? (latestMessage.createdAt ?? latestMessage.created_at ?? null) : null,
      previewText: buildMessagePreviewText(latestMessage),
    });
  }, [messages, currentUser, channelId, updateChannelSummary, pendingUnreadIds.length]);

  useEffect(() => {
    if (!messages || !Array.isArray(messages.data) || messages.data.length === 0) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const attemptScrollToUnread = () => {
      const unreadId = firstUnreadIdRef.current;
      if (unreadId) {
        const node = messageRefs.current.get(String(unreadId));
        if (node) {
          node.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
          firstUnreadIdRef.current = null;
          return true;
        }
      }
      return false;
    };

    if (preserveScrollOnNextMessagesRenderRef.current) {
      preserveScrollOnNextMessagesRenderRef.current = false;
      return;
    }

    if (attemptScrollToUnread()) return;

    const handle = window.requestAnimationFrame(() => {
      if (!attemptScrollToUnread() && container) {
        container.scrollTop = container.scrollHeight;
      }
    });

    return () => window.cancelAnimationFrame(handle);
  }, [messages]);

  useLayoutEffect(() => {
    const anchor = pendingHistoryAnchorRef.current;
    if (!anchor?.id) return;
    const container = messagesContainerRef.current;
    if (!container) {
      pendingHistoryAnchorRef.current = null;
      return;
    }
    const node = messageRefs.current.get(String(anchor.id));
    if (!node) {
      pendingHistoryAnchorRef.current = null;
      return;
    }
    container.scrollTop = node.offsetTop - anchor.offset;
    pendingHistoryAnchorRef.current = null;
  }, [messages]);

  useEffect(() => {
    if (vipView !== 'conversation') return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const list = messagesStateRef.current?.data || messages?.data;
    if (!Array.isArray(list) || list.length === 0) return;

    const handle = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });

    return () => window.cancelAnimationFrame(handle);
  }, [vipView, channelId]);

  // Ensure backend read-state is synced at least to the latest loaded message
  useEffect(() => {
    if (!channelId) return;
    const list = messagesStateRef.current?.data || messages?.data || [];
    if (!Array.isArray(list) || list.length === 0) return;
    const latest = list[list.length - 1];
    const latestId = getMessageIdentifier(latest);
    if (!latestId) return;
    const key = `${channelId}:${String(latestId)}`;
    if (lastReadSyncKeyRef.current === key) return;

    (async () => {
      try {
        await ChannelMessagesService.markAsRead(channelId, [String(latestId)]);
        updateChannelSummary({ unreadCount: 0, readState: { lastReadAt: latest.createdAt || latest.created_at || null } });
        signalGlobalUnreadUpdate();
        signalNotificationRefresh();
        lastReadSyncKeyRef.current = key;
      } catch (err) {
        // non-fatal
      }
    })();
  }, [channelId, messages, updateChannelSummary, signalGlobalUnreadUpdate, signalNotificationRefresh]);

  useEffect(() => {
    if (!channelId || pendingUnreadIds.length === 0) return;
    if (!currentUserId) {
      setPendingUnreadIds([]);
      return;
    }
    const idsToMark = pendingUnreadIds.slice();
    let cancelled = false;

    async function markAsRead() {
      let readState = null;
      try {
        if (typeof ChannelMessagesService.markAsRead === 'function') {
          const result = await ChannelMessagesService.markAsRead(channelId, idsToMark);
          readState = result?.data?.readState || null;
        }
      } catch (err) {
        // silencioso
      } finally {
        if (!cancelled && isMountedRef.current) {
          setMessages((prev) => {
            if (!prev?.data) return prev;
            const updatedData = prev.data.map((msg) =>
              idsToMark.includes(msg?._id)
                ? markChannelMessageAsReadLocally(msg, currentUserId)
                : msg
            );
            const payload = { ...prev, data: updatedData };
            messagesStateRef.current = payload;
            return payload;
          });

          const fallbackLastRead = (() => {
            const list = messagesStateRef.current?.data || [];
            const markedMessages = list.filter((msg) => idsToMark.includes(msg?._id));
            if (!markedMessages.length) return null;
            const newest = markedMessages.reduce((acc, item) => {
              if (!acc) return item;
              return new Date(item.createdAt).getTime() > new Date(acc.createdAt).getTime() ? item : acc;
            }, null);
            return newest?.createdAt || newest?.created_at || null;
          })();

          updateChannelSummary({
            unreadCount: 0,
            readState: readState || (fallbackLastRead ? { lastReadAt: fallbackLastRead } : null),
          });
          signalGlobalUnreadUpdate();
          signalNotificationRefresh();
          setPendingUnreadIds([]);
        }
      }
    }

    markAsRead();
    return () => { cancelled = true; };
  }, [pendingUnreadIds, channelId, currentUserId, updateChannelSummary, signalGlobalUnreadUpdate, signalNotificationRefresh]);

  const registerMessageRef = useCallback((id, node) => {
    if (!id) return;
    const key = String(id);
    if (node) {
      messageRefs.current.set(key, node);
    } else {
      messageRefs.current.delete(key);
    }
  }, []);

  const handleNavigateToMessage = useCallback((messageId) => {
    if (!messageId) return;
    const id = String(messageId);
    const targetNode = messageRefs.current.get(id);
    if (!targetNode) return;

    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    setTargetMessageId(id);

    if (targetMessageTimeoutRef.current) {
      window.clearTimeout(targetMessageTimeoutRef.current);
    }
    targetMessageTimeoutRef.current = window.setTimeout(() => {
      setTargetMessageId((current) => (current === id ? null : current));
      targetMessageTimeoutRef.current = null;
    }, 1700);
  }, []);

  const buildReplyPayload = useCallback((message) => {
    if (!message || typeof message !== 'object') return null;
    const messageId = getMessageIdentifier(message);
    if (!messageId) return null;
    const sender =
      (message.message_sender && typeof message.message_sender === 'object' && message.message_sender)
      || (message.user && typeof message.user === 'object' && message.user)
      || null;
    const senderId = resolveUserId(sender) || resolveUserId(message.message_sender) || resolveUserId(message.user) || null;
    const senderUsername =
      sender?.user_username
      || message?.message_sender_username
      || message?.user_username
      || null;
    const messageText = typeof message?.message_text === 'string'
      ? message.message_text.trim()
      : typeof message?.text === 'string'
        ? message.text.trim()
        : '';
    const mediaType = String(message?.message_media?.type || message?.media?.type || '').toLowerCase();
    const hasMedia = Boolean(message?.message_media || message?.media);
    const hasSticker = Boolean(message?.message_sticker || message?.sticker);
    const previewText = messageText
      || (hasSticker ? 'Sticker' : hasMedia ? (mediaType === 'video' ? 'Video' : mediaType === 'audio' ? 'Audio' : 'Image') : 'Message');
    return {
      messageId: String(messageId),
      senderId: senderId ? String(senderId) : null,
      senderUsername: senderUsername || 'Unknown user',
      previewText,
      createdAt: message?.createdAt || message?.created_at || null,
    };
  }, []);

  const activeReplyPayload = useMemo(
    () => buildReplyPayload(replyToMessage),
    [buildReplyPayload, replyToMessage]
  );

  const handleReplyToMessage = useCallback((message) => {
    setReplyToMessage(message || null);
  }, []);

  const sendMessage = useCallback(async (payload) => {
    if (!channelId || !currentUser) return;

    const normalizedPayload = typeof payload === 'string'
      ? { text: payload }
      : (payload && typeof payload === 'object' ? { ...payload } : {});

    const text = typeof normalizedPayload.text === 'string' ? normalizedPayload.text.trim() : '';
    const sticker = normalizedPayload.sticker || null;
    const mediaListInput = Array.isArray(normalizedPayload.mediaList) ? normalizedPayload.mediaList.filter(Boolean) : [];
    const singleMedia = normalizedPayload.media || null;
    const combinedMediaList = mediaListInput.length > 0
      ? mediaListInput
      : (singleMedia ? [singleMedia] : []);
    const replyPayload = buildReplyPayload(normalizedPayload.replyTo || replyToMessage);

    if (!text && !sticker && combinedMediaList.length === 0) return;

    const tempId = uuidv4();
    const optimisticSender = cloneUser(currentUser);
    const optimisticPayload = {
      _id: tempId,
      clientSideId: tempId,
      isSending: true,
      createdAt: Date.now(),
      message_text: text,
      text,
      message_sticker: sticker || undefined,
      message_media: combinedMediaList.length === 1 ? combinedMediaList[0] : undefined,
      media: combinedMediaList.length === 1 ? combinedMediaList[0] : undefined,
      mediaList: combinedMediaList.length > 1 ? combinedMediaList : undefined,
      attachment: combinedMediaList.length > 1 ? combinedMediaList : undefined,
      message_sender: optimisticSender,
      user: optimisticSender,
      metadata: replyPayload
        ? { ...(normalizedPayload.metadata && typeof normalizedPayload.metadata === 'object' ? normalizedPayload.metadata : {}), reply: replyPayload }
        : normalizedPayload.metadata,
    };

    const optimistic = normalizeChannelMessage(optimisticPayload, { fallbackSender: optimisticSender, currentUser });

    setMessages((prev) => {
      const previousData = Array.isArray(prev?.data) ? prev.data : [];
      const data = [...previousData, optimistic];
      const payloadState = {
        data,
        totalDocs: (prev?.totalDocs ?? previousData.length) + 1,
      };
      messagesStateRef.current = payloadState;
      return payloadState;
    });

    firstUnreadIdRef.current = null;
    lastUnreadKeyRef.current = '';
    setPendingUnreadIds([]);
    setFirstUnreadId(null);

    const apiPayload = {};
    if (text) apiPayload.text = text;
    if (sticker) apiPayload.sticker = sticker;
    if (normalizedPayload.stickerId) apiPayload.stickerId = normalizedPayload.stickerId;
    if (combinedMediaList.length > 1) apiPayload.mediaList = combinedMediaList;
    else if (combinedMediaList.length === 1) apiPayload.media = combinedMediaList[0];
    const metadataPayload = normalizedPayload.metadata && typeof normalizedPayload.metadata === 'object'
      ? { ...normalizedPayload.metadata }
      : {};
    if (replyPayload) {
      metadataPayload.reply = replyPayload;
      setReplyToMessage(null);
    }
    if (Object.keys(metadataPayload).length > 0) {
      apiPayload.metadata = metadataPayload;
    }

    try {
      const { response, data } = await ChannelMessagesService.create(channelId, apiPayload);
      if (response?.ok) {
        const serverMessageLike = data?.data || data?.message || data?.result || data || null;
        if (serverMessageLike) {
          let normalizedServerMessage = normalizeChannelMessage(serverMessageLike, { currentUser });
          const hasServerReplyContext = Boolean(
            normalizedServerMessage?.metadata?.reply
            || normalizedServerMessage?.message_metadata?.reply
            || normalizedServerMessage?.reply
            || normalizedServerMessage?.message_reply
          );
          if (normalizedServerMessage && replyPayload && !hasServerReplyContext) {
            normalizedServerMessage = {
              ...normalizedServerMessage,
              metadata: {
                ...(normalizedServerMessage.metadata && typeof normalizedServerMessage.metadata === 'object'
                  ? normalizedServerMessage.metadata
                  : {}),
                reply: replyPayload,
              },
            };
          }
          if (normalizedServerMessage) {
            normalizedServerMessage = {
              ...normalizedServerMessage,
              clientSideId: tempId,
              isSending: false,
            };
          }
          setMessages((prev) => {
            const previousData = Array.isArray(prev?.data) ? prev.data : [];
            let replaced = false;
            const merged = previousData.map((msg) => {
              const identifier = getMessageIdentifier(msg);
              const clientSide = msg?.clientSideId;
              if (identifier === tempId || clientSide === tempId) {
                replaced = true;
                return normalizedServerMessage;
              }
              return msg;
            }).filter(Boolean);
            if (normalizedServerMessage && !replaced) {
              merged.push(normalizedServerMessage);
            }
            merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            const payloadState = { data: merged, totalDocs: merged.length };
            messagesStateRef.current = payloadState;
            return payloadState;
          });
        } else {
          setMessages((prev) => {
            const previousData = Array.isArray(prev?.data) ? prev.data : [];
            const filtered = previousData.filter((msg) => msg?.clientSideId !== tempId && getMessageIdentifier(msg) !== tempId);
            const payloadState = { data: filtered, totalDocs: filtered.length };
            messagesStateRef.current = payloadState;
            return payloadState;
          });
        }

        await fetchMessages({ reset: false, force: true });
        signalGlobalUnreadUpdate();
      } else if (response?.status === 401) {
        setError('Unauthorized. Please ensure you are logged in.');
      }
    } catch (err) {
      setMessages((prev) => {
        const previousData = Array.isArray(prev?.data) ? prev.data : [];
        const data = previousData.map((msg) => {
          const identifier = getMessageIdentifier(msg);
          const clientSide = msg?.clientSideId;
          if (identifier === tempId || clientSide === tempId) {
            return { ...msg, isSending: false };
          }
          return msg;
        });
        const payloadState = { ...prev, data, totalDocs: data.length };
        messagesStateRef.current = payloadState;
        return payloadState;
      });
      setError('Failed to send message.');
    }
  }, [channelId, currentUser, fetchMessages, signalGlobalUnreadUpdate, buildReplyPayload, replyToMessage]);

  const handleMediaSelected = useCallback(async (files) => {
    if (!channelId || !canShareMedia) return;
    const fileList = Array.isArray(files) ? files : Array.from(files || []);
    if (!fileList.length) return;

    setMediaUploadInProgress(true);
    try {
      for (const file of fileList) {
        if (!file) continue;
        const mimeType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
        const mediaType = mimeType.startsWith('video') ? 'video' : mimeType.startsWith('image') ? 'image' : null;
        if (!mediaType) {
          setError('Solo se permiten imagenes o videos.');
          continue;
        }

        const { response, data } = await ChannelMessagesService.requestDirectUpload(channelId, {
          type: mediaType,
          mimeType: file.type,
          fileName: file.name,
          fileSize: typeof file.size === 'number' ? file.size : undefined,
        });

        if (!response?.ok) {
          throw new Error('upload-request-failed');
        }

        const payload = data?.data || data || {};
        const uploadUrl = payload.uploadURL || payload.uploadUrl || payload.upload_url || null;
        if (!uploadUrl) {
          throw new Error('missing-upload-url');
        }

        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error('upload-failed');
        }

        const mediaPayload = {
          type: mediaType,
          assetId: payload.assetId || payload.asset_id || payload.id || null,
          playbackUrl: payload.playbackUrl || payload.playback_url || payload.deliveryUrl || payload.delivery_url || null,
          previewUrl: payload.previewUrl || payload.preview_url || payload.thumbnailUrl || payload.thumbnail_url || null,
          thumbnailUrl: payload.thumbnailUrl || payload.thumbnail_url || payload.previewUrl || payload.preview_url || null,
          durationSeconds: payload.durationSeconds || payload.duration || payload.duration_seconds || null,
          mimeType: payload.mimeType || payload.contentType || file.type || null,
        };

        await sendMessage({ media: mediaPayload });
      }
    } catch (err) {
      console.error('channel-chat: media upload failed', err);
      setError('No se pudo adjuntar el archivo multimedia.');
    } finally {
      setMediaUploadInProgress(false);
    }
  }, [channelId, canShareMedia, sendMessage]);

  useEffect(() => {
    let cancelled = false;

    if (vipView === 'conversation') {
      setVipContent((prev) => (
        prev.loading || prev.error
          ? { ...prev, loading: false, error: null }
          : prev
      ));
      return () => {
        cancelled = true;
      };
    }

    if (!ownerMeta?.ownerId) {
      setVipContent({ loading: false, error: null, data: [], totalDocs: 0 });
      return () => {
        cancelled = true;
      };
    }

    const ownerType = resolveChannelOwnerProfileType(ownerMeta?.ownerType, 'gallery');
    const endpoint = `${config.apis.api.url}/posts/${ownerType}/${vipView}/${ownerMeta.ownerId}`;

    setVipContent((prev) => ({ ...prev, loading: true, error: null }));

    fetchWrapper
      .get(endpoint, { injectToken: true })
      .then(({ response, data }) => {
        if (cancelled) return;
        if (!response?.ok) {
          setVipContent({ loading: false, error: 'Failed to load VIP content.', data: [], totalDocs: 0 });
          return;
        }
        const list = Array.isArray(data?.data) ? data.data : [];
        const vipOnlyList = list.filter((item) => isVipOnlyAudience(item));
        setVipContent({
          loading: false,
          error: null,
          data: vipOnlyList,
          totalDocs: vipOnlyList.length,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setVipContent({ loading: false, error: 'Failed to load VIP content.', data: [], totalDocs: 0 });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ownerMeta?.ownerId, ownerMeta?.ownerType, vipView]);

  useEffect(() => {
    if (vipView !== 'conversation') {
      setReplyToMessage(null);
      setShowOlderMessagesLoader(false);
      setLoadingOlderMessages(false);
      loadingOlderMessagesRef.current = false;
      if (olderLoaderDelayTimeoutRef.current) {
        window.clearTimeout(olderLoaderDelayTimeoutRef.current);
        olderLoaderDelayTimeoutRef.current = null;
      }
    }
  }, [vipView]);

  useEffect(() => {
    setReplyToMessage(null);
  }, [channelId]);

  const messagesList = Array.isArray(messages?.data) ? messages.data : [];

  const conversationInputNode = (
    <ConversationInput
      sendMessage={sendMessage}
      stickers={channelStickers}
      stickersLoading={channelStickersLoading}
      galleryId={channelId}
      allowMediaUploads={canShareMedia}
      onMediaSelect={canShareMedia ? handleMediaSelected : undefined}
      mediaUploading={mediaUploadInProgress}
      replyToMessage={activeReplyPayload}
      onClearReply={() => setReplyToMessage(null)}
    />
  );

  function shouldRenderTimestamp(index, list) {
    if (index === 0) return true;
    const currentTime = new Date(list[index].createdAt).getTime();
    const previousTime = new Date(list[index - 1].createdAt).getTime();
    return !Utils.isSameDay(currentTime, previousTime);
  }

  const infoPath = useMemo(() => {
    if (!channelOwner || !channelId) return null;
    const base = `/channel/${channelOwner}/${channelId}/info`;
    if (vipView === 'posts' || vipView === 'articles') {
      return `${base}?view=${vipView}`;
    }
    return base;
  }, [channelId, channelOwner, vipView]);
  const conversationPath = channelOwner && channelId ? `/channel/${channelOwner}/${channelId}` : null;
  const { loungePostsPath, loungeArticlesPath, conversationTabPath } = useMemo(() => {
    if (!conversationPath) {
      return { loungePostsPath: null, loungeArticlesPath: null, conversationTabPath: null };
    }
    const baseParams = new URLSearchParams(location.search || '');

    const withView = (view) => {
      const params = new URLSearchParams(baseParams);
      if (view === 'conversation') {
        params.delete('view');
      } else {
        params.set('view', view);
      }
      const query = params.toString();
      return query ? `${conversationPath}?${query}` : conversationPath;
    };

    return {
      loungePostsPath: withView('posts'),
      loungeArticlesPath: withView('articles'),
      conversationTabPath: withView('conversation'),
    };
  }, [conversationPath, location.search]);

  const handleNavigateToInfo = useCallback(() => {
    if (!infoPath) return;
    navigate(infoPath, {
      state: {
        from: location?.state?.from || null,
        fromChat: true,
        fromVipView: vipView,
      },
    });
  }, [infoPath, navigate, location?.state?.from, vipView]);

  const supportPopupNode = (
    <SupportPublicPopup
      open={canShowSupportIcon && showSupportPopup}
      onClose={() => setShowSupportPopup(false)}
      config={supportConfig || defaultSupportConfig}
      supportedUser={supportPopupUser}
      onConfirm={handleSupportConfirm}
      isSubmitting={supportRequest.loading}
      errorMessage={supportRequest.error}
    />
  );

  const header = (
    <>
      <div className="channel-chat__header">
        <BackArrowIcon
          className="channel-chat__back-button"
          onClick={handleBack}
        />
        <Link className="no-underline" to={`/user/${channelOwner}`} state={{ from: true }}>
          <UserProfileImage image={channelImage} typeOfProfile={channelOwnerProfileType} className="chat-message__user-image" />
        </Link>
        <button
          type="button"
          className="channel-chat__title-button"
          onClick={handleNavigateToInfo}
          disabled={!infoPath}
          title={infoPath ? 'Ver informacion del canal' : undefined}
        >
          <h2>{channelName}</h2>
          {channelCreatorUsername ? (
            <span className="channel-chat__creator">by {channelCreatorUsername}</span>
          ) : null}
        </button>
        {(canShowSupportIcon || infoPath) ? (
          <div className="channel-chat__header-actions">
            {canShowSupportIcon ? (
              <button
                type="button"
                className="channel-chat__support-button"
                onClick={() => setShowSupportPopup(true)}
                title={t('actions.support')}
                aria-label={t('actions.support')}
              >
                <SupportPresentIcon className="channel-chat__support-icon-svg" />
              </button>
            ) : null}
            {infoPath ? (
              <button
                type="button"
                className="channel-chat__info-button"
                onClick={handleNavigateToInfo}
                title="Informacion del canal"
              >
                <InfoIcon />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {supportPopupNode}
    </>
  );

  const vipNavigation = (
    <div className={`channel-chat__vip-navigation element-non-selectable ${vipView === 'conversation' ? 'channel-chat__vip-navigation--conversation' : ''}`.trim()}>
      {conversationTabPath ? (
        <Link
          className={`channel-chat__vip-navigation-link ${vipView === 'conversation' ? 'active' : ''}`}
          to={conversationTabPath}
          state={location?.state || {}}
        >
          Channel
        </Link>
      ) : (
        <span className="channel-chat__vip-navigation-link disabled">Channel</span>
      )}
      {loungePostsPath ? (
        <Link
          className={`channel-chat__vip-navigation-link ${vipView === 'posts' ? 'active' : ''}`}
          to={loungePostsPath}
          state={location?.state || {}}
        >
          Posts
        </Link>
      ) : (
        <span className="channel-chat__vip-navigation-link disabled">Posts</span>
      )}
      {loungeArticlesPath ? (
        <Link
          className={`channel-chat__vip-navigation-link ${vipView === 'articles' ? 'active' : ''}`}
          to={loungeArticlesPath}
          state={location?.state || {}}
        >
          Articles
        </Link>
      ) : (
        <span className="channel-chat__vip-navigation-link disabled">Articles</span>
      )}
    </div>
  );

  const vipContentNode = (
    <div className="channel-chat__vip-content">
      {vipContent.loading && (
        <div className="channel-chat__vip-content-loader">
          <Loader inline thin />
        </div>
      )}
      {!vipContent.loading && vipContent.error && (
        <div className="channel-chat__vip-content-empty">{vipContent.error}</div>
      )}
      {!vipContent.loading && !vipContent.error && vipContent.data.length === 0 && (
        <div className="channel-chat__vip-content-empty">
          No {vipView} available in this VIP lounge yet.
        </div>
      )}
      {!vipContent.loading && !vipContent.error && vipContent.data.length > 0 && (
        vipView === 'posts' ? (
          <PostVirtualList
            items={{ data: vipContent.data, totalDocs: vipContent.totalDocs }}
            onLoadMore={() => {}}
          />
        ) : (
          <BoardVirtualList
            items={{ data: vipContent.data, totalDocs: vipContent.totalDocs }}
            onLoadMore={() => {}}
            shouldLoadMore={false}
            listLength={vipContent.data.length}
          />
        )
      )}
    </div>
  );

  if (vipView !== 'conversation') {
    return (
      <div className="channel-chat__container">
        {header}
        {vipNavigation}
        {vipContentNode}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="channel-chat__container">
        {header}
        {vipNavigation}
      </div>
    );
  }
  if (error) {
    return (
      <div className="channel-chat__container">
        {header}
        {vipNavigation}
        <div className="channel-chat__error">{error}</div>
        <div className="channel-chat__input">
          {conversationInputNode}
        </div>
      </div>
    );
  }
  if (!messagesList || messagesList.length === 0) {
    return (
      <div className="channel-chat__container">
        {header}
        {vipNavigation}
        <EmptyChannel channelData={{ user_username: channelOwner, owner_username: channelOwner, isOwner }} />
        <div className="channel-chat__input">
          {conversationInputNode}
        </div>
      </div>
    );
  }

  return (
    <div className="channel-chat__container">
      {header}
      {vipNavigation}
      <div className="channel-chat__history-loader">
        {(showOlderMessagesLoader && loadingOlderMessages) ? <Loader inline thin /> : null}
      </div>
      <div className="channel-chat__messages" ref={messagesContainerRef} onScroll={handleConversationScroll}>
        {messagesList.map((message, index) => {
          if (!message) return null;
          const messageKey = message.clientSideId || message._id || `message-${index}`;
          const resolvedIdentifier = getMessageIdentifier(message);
          const messageTimestamp = new Date(message.createdAt ?? message.created_at ?? Date.now()).getTime();
          const isValidTimestamp = !Number.isNaN(messageTimestamp);
          const displayTimestamp = isValidTimestamp ? messageTimestamp : Date.now();
          const senderId =
            resolveUserId(message.message_sender)
            || resolveUserId(message.user)
            || null;
          const senderUsername =
            message?.message_sender?.user_username
            || message?.user?.user_username
            || message?.message_sender_username
            || message?.sender_username
            || null;
          const isMineMessage =
            Boolean(currentUserId)
            && Boolean(senderId)
            && String(currentUserId) === String(senderId);
          const isOwnerMessage =
            (Boolean(ownerMeta?.ownerId) && Boolean(senderId) && String(ownerMeta.ownerId) === String(senderId))
            || (Boolean(channelOwner) && Boolean(senderUsername) && String(channelOwner).toLowerCase() === String(senderUsername).toLowerCase());
          const withinDeleteWindow = isValidTimestamp ? (Date.now() - messageTimestamp <= MESSAGE_DELETE_WINDOW_MS) : true;
          const messageIdString = resolvedIdentifier ? String(resolvedIdentifier) : null;
          const isSendingMessage = Boolean(message?.isSending);
          const canDeleteMessage = Boolean(!isSendingMessage && resolvedIdentifier && (hasModerationPermission || (isMineMessage && withinDeleteWindow)));
          const isDeletingMessage = messageIdString ? deletingMessageIds.has(messageIdString) : false;
          const isUnreadBoundary = Boolean(unreadMarkerId && messageIdString && unreadMarkerId === messageIdString);
          return (
            <div
              key={messageKey}
              ref={(node) => {
                registerMessageRef(messageKey, node);
                if (messageIdString && messageIdString !== String(messageKey)) {
                  registerMessageRef(messageIdString, node);
                }
              }}
              style={{ paddingBottom: '15px' }}
            >
              {shouldRenderTimestamp(index, messagesList) && (
                <div style={{ paddingBottom: '15px' }}>
                  <ChatTimestamp timestamp={displayTimestamp} />
                </div>
              )}
              {isUnreadBoundary && (
                <div className="channel-chat__unread-divider">
                  <span>Unread messages</span>
                </div>
              )}
              <ChatMessage
                messageData={message}
                showAvatar={true}
                isMineRight={true}
                isTargeted={Boolean(targetMessageId && messageIdString === targetMessageId)}
                onNavigateToMessage={handleNavigateToMessage}
                canReply={true}
                onReply={handleReplyToMessage}
                canDelete={canDeleteMessage}
                onDelete={canDeleteMessage ? deleteMessage : undefined}
                deleting={isDeletingMessage}
                sending={isSendingMessage}
                profileTypeOverride={isOwnerMessage ? channelOwnerProfileType : null}
              />
            </div>
          );
        })}
      </div>
      <div className="channel-chat__input">
        {conversationInputNode}
      </div>
    </div>
  );
}
